import React, { useContext, useEffect } from 'react';
import { Box, Typography, Button, Dialog } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import AccountTreeOutlinedIcon from '@material-ui/icons/AccountTreeOutlined';
import StarsOutlinedIcon from '@material-ui/icons/StarsOutlined';
import AssignmentTurnedInOutlinedIcon from '@material-ui/icons/AssignmentTurnedInOutlined';
import PaletteOutlinedIcon from '@material-ui/icons/PaletteOutlined';
import CallMadeIcon from '@material-ui/icons/CallMade';
import AssignmentOutlinedIcon from '@material-ui/icons/AssignmentOutlined';
import { useHistory } from 'react-router-dom';
import ProgramInfoIcon from '../../assets/images/DQProImages/product-info-icon.png';
import AccessInfoIcon from '../../assets/images/DQProImages/assess-info-icon.png';
import {
    clearChatStateInsiderService,
    getActiveChatInsiderProgramService,
} from '../../services/learning_programs.service';
import { AuthContext } from '../../context/auth';
import {PRODUCT_KEY, ASSESSMENT_TYPE, USER_GROUPS, DQ_PRO_PLAN_KEY} from '../../utils/constants';
import { isProductEnabled } from '../../utils/productAccess';
import { listProgramRequestsService } from "../../services/client.service";
import {COS_PREVIEW_PATH} from "../../utils/cpci.util";

// The CPC Index assessment is fixed to the "YoungPeople" audience — no audience picker.
const CPCI_ITEM = {
    assessment_type: ASSESSMENT_TYPE.CPCI,
    user_group: USER_GROUPS.YOUNGPEOPLE,
};

/* ─── shared token ─── */
// Driven by the workspace branding (falls back to the DQ orange).
const ORANGE = 'var(--brand-primary, #FF6900)';

/* ─── feature card ─── */
const FeatureItem = ({ title, desc }) => (
    <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
        <Box
            sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: ORANGE,
                flexShrink: 0,
                mt: '7px',
            }}
        />
        <Typography sx={{ fontSize: '14px', color: 'var(--brand-text, #374151)', lineHeight: 1.6 }}>
            <strong style={{ color: 'var(--brand-text, #111827)' }}>{title}:</strong> {desc}
        </Typography>
    </Box>
);

/* ─── feature grid tile (icon + bold title + desc, in a bordered box) ─── */
const FeatureGridItem = ({ icon: Icon, title, desc }) => (
    <Box
        sx={{
            flex: '1 1 calc(50% - 8px)',
            minWidth: 200,
            backgroundColor: 'var(--brand-surface-subtle, #F9FAFB)',
            border: '1px solid var(--brand-border, #EEF0F2)',
            borderRadius: '12px',
            p: 2,
        }}
    >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
            {Icon && <Icon sx={{ fontSize: 18, color: ORANGE }} />}
            <Typography sx={{ fontSize: '14px', fontWeight: 700, color: 'var(--brand-text, #111827)' }}>
                {title}
            </Typography>
        </Box>
        <Typography sx={{ fontSize: '13px', color: 'var(--brand-text-secondary, #6B7280)', lineHeight: 1.5 }}>
            {desc}
        </Typography>
    </Box>
);

/* ─── section block (grouped bullets, e.g. "What It Includes") ─── */
const SectionBlock = ({ heading, items }) => (
    <Box sx={{ mb: 2.5 }}>
        <Typography sx={{ fontSize: '12px', fontWeight: 700, letterSpacing: '0.4px', textTransform: 'uppercase', color: 'var(--brand-text, #111827)', mb: 1 }}>
            {heading}
        </Typography>
        {items.map((item, i) => (
            <Box key={i} sx={{ display: 'flex', gap: 1.2, mb: 0.75 }}>
                <Box
                    sx={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        backgroundColor: ORANGE,
                        flexShrink: 0,
                        mt: '7px',
                    }}
                />
                <Typography sx={{ fontSize: '14px', color: 'var(--brand-text, #374151)', lineHeight: 1.6 }}>
                    {item}
                </Typography>
            </Box>
        ))}
    </Box>
);

/* ─── main card ─── */
const CreateCard = ({
    icon, badgeColor, title, subtitle, description, featuresHeading,
    features, featureGrid, sections, ctaLabel, onCta,
    secondaryCtaLabel, onSecondaryCta, singleCard,
}) => (
    <Box
        sx={{
            width: '100%',
            flex: { md: '1 1 0' },
            minWidth: 0,
            // A lone card would otherwise be pinned to a narrow half-width column — give it room.
            maxWidth: singleCard ? { xs: '100%', md: 720 } : { md: 'calc(50% - 12px)' },
            border: '1px solid var(--brand-border, #E5E7EB)',
            borderRadius: '20px',
            boxShadow: '0px 4px 16px rgba(0,0,0,0.06)',
            backgroundColor: 'var(--brand-surface, #ffffff)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
        }}
    >
        {/* Card header strip */}
        <Box
            sx={{
                backgroundColor: badgeColor,
                px: 3,
                py: 1.5,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
            }}
        >
            <Typography sx={{ color: '#fff', fontSize: '13px', fontWeight: 700, letterSpacing: '0.4px', textTransform: 'uppercase' }}>
                {subtitle}
            </Typography>
        </Box>

        {/* Card body */}
        <Box sx={{ p: { xs: 3, sm: 4 }, display: 'flex', flexDirection: 'column', flex: 1 }}>
            {/* Icon ABOVE the title, both centred. Side by side squeezed the title
                into a narrow column and read as cramped (Lydia, slide 42). */}
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, mb: 3 }}>
                <Box
                    sx={{
                        width: 80,
                        height: 80,
                        borderRadius: '16px',
                        backgroundColor: 'var(--brand-surface, #FFF5EE)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                    }}
                >
                    <img src={icon} alt={title} style={{ width: 52, height: 52, objectFit: 'contain' }} />
                </Box>
                <Typography sx={{ fontSize: { xs: '18px', sm: '22px' }, fontWeight: 700, color: 'var(--brand-text, #111827)', lineHeight: 1.3, textAlign: 'center' }}>
                    {title}
                </Typography>
            </Box>

            {/* Intro paragraph(s) */}
            {description && (
                <Box sx={{ mb: 3 }}>
                    {(Array.isArray(description) ? description : [description]).map((p, i, arr) => (
                        <Typography
                            key={i}
                            sx={{ fontSize: '14px', color: 'var(--brand-text-secondary, #4B5563)', lineHeight: 1.6, mb: i < arr.length - 1 ? 1.5 : 0 }}
                        >
                            {p}
                        </Typography>
                    ))}
                </Box>
            )}

            {featuresHeading && (
                <Typography sx={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.6px', textTransform: 'uppercase', color: 'var(--brand-text-muted, #9CA3AF)', mb: 1.5 }}>
                    {featuresHeading}
                </Typography>
            )}

            {/* Feature bullets / grid / section blocks */}
            <Box sx={{ flexGrow: 1, mb: sections ? 1.5 : 3 }}>
                {sections
                    ? sections.map((s, i) => (
                        <SectionBlock key={i} heading={s.heading} items={s.items} />
                    ))
                    : featureGrid
                        ? (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                                {features.map((f, i) => (
                                    <FeatureGridItem key={i} icon={f.icon} title={f.title} desc={f.desc} />
                                ))}
                            </Box>
                        )
                        : features.map((f, i) => (
                            <FeatureItem key={i} title={f.title} desc={f.desc} />
                        ))}
            </Box>

            {/* CTA */}
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                <Button
                    onClick={onCta}
                    endIcon={secondaryCtaLabel ? <CallMadeIcon sx={{ fontSize: 16 }} /> : undefined}
                    sx={{
                        alignSelf: 'flex-start',
                        backgroundColor: ORANGE,
                        color: '#fff',
                        textTransform: 'none',
                        fontWeight: 700,
                        fontSize: '15px',
                        px: 4,
                        py: 1.25,
                        borderRadius: '10px',
                        boxShadow: 'none',
                        '&:hover': { backgroundColor: '#e55e00', boxShadow: 'none' },
                    }}
                >
                    {ctaLabel}
                </Button>
                {secondaryCtaLabel && (
                    <Button
                        onClick={onSecondaryCta}
                        variant="outlined"
                        sx={{
                            alignSelf: 'flex-start',
                            borderColor: 'var(--brand-border, #D1D5DB)',
                            color: 'var(--brand-text, #111827)',
                            textTransform: 'none',
                            fontWeight: 700,
                            fontSize: '15px',
                            px: 4,
                            py: 1.25,
                            borderRadius: '10px',
                            boxShadow: 'none',
                            '&:hover': { borderColor: 'var(--brand-text-muted, #9CA3AF)', backgroundColor: 'transparent' },
                        }}
                    >
                        {secondaryCtaLabel}
                    </Button>
                )}
            </Box>
        </Box>
    </Box>
);

/* ─── page ─── */
const DQProCreate = () => {
    const history = useHistory();
    const { authUser } = useContext(AuthContext);
    const hasLearningPrograms = isProductEnabled(authUser, PRODUCT_KEY.LEARNING_PROGRAM);
    const hasAssessments = isProductEnabled(authUser, PRODUCT_KEY.ASSESSMENT);
    const hasCpciAssessments = isProductEnabled(authUser, PRODUCT_KEY.CPC_INDEX_ASSESSMENT);
    // The school→partner request loop ships with enterprise_p1 only, so the
    // requests card is plan-gated as well as product-gated: an enterprise_p0
    // owner with LearnCreator still must not see it.
    const hasProgramRequests = hasLearningPrograms
        && authUser?.dq_pro_plan?.plan_key === DQ_PRO_PLAN_KEY.ENTERPRISE_P1;
    // Card count includes the requests card when it is shown, so a lone
    // LearnCreator is never treated as a single card.
    const visibleCardCount = [hasLearningPrograms, hasAssessments, hasCpciAssessments].filter(Boolean).length
        + (hasProgramRequests ? 1 : 0);
    const singleCard = visibleCardCount === 1;

    const [hasExistingProgram, setHasExistingProgram] = React.useState(false);
    const [showExistingDialog, setShowExistingDialog] = React.useState(false);
    // Slide 180 — count of learning-program requests from schools, shown in a
    // banner above the cards. Only fetched for owners who have LearnCreator.
    const [requestCount, setRequestCount] = React.useState(null);
    const isStartingNewRef = React.useRef(false);

    useEffect(() => {
        let isMounted = true;
        getActiveChatInsiderProgramService()
            .then(res => { if (isMounted) setHasExistingProgram(!!res?.data?.program_id); })
            .catch(() => {});
        return () => { isMounted = false; };
    }, []);

    useEffect(() => {
        // Plan-gated, so an enterprise_p0 workspace never even makes this call.
        if (!hasProgramRequests) return undefined;
        let alive = true;
        listProgramRequestsService({ page: 1, limit: 1 })
            .then(res => { if (alive) setRequestCount(Number(res?.data?.total) || 0); })
            .catch(() => {});
        return () => { alive = false; };
    }, [hasProgramRequests]);

    const handleStartNew = async () => {
        if (isStartingNewRef.current) return;
        isStartingNewRef.current = true;
        try {
            const res = await getActiveChatInsiderProgramService();
            const programId = res?.data?.program_id;
            if (programId) {
                try { await clearChatStateInsiderService(programId); } catch (e) {}
            }
        } catch (e) {}
        finally {
            setShowExistingDialog(false);
            history.push('/dq_pro/program/creation/preview');
            isStartingNewRef.current = false;
        }
    };

    const handleCreateProgram = () => {
        if (hasExistingProgram) {
            setShowExistingDialog(true);
        } else {
            handleStartNew();
        }
    };

    const handleCreateAssessment = () => {
        history.push('/dq_pro/products/assessments');
    };

    // Preview is read-only: open the COS catalogue preview directly. Nothing is
    // built, so no credits/tokens are needed (works on an empty balance too).
    const handlePreview = () => {
        history.push({
            pathname: COS_PREVIEW_PATH,
            state: { fromOnboarding: true },
        });
    };

    const programFeatures = [
        { icon: AccountTreeOutlinedIcon, title: 'Program & syllabus', desc: 'Complete program architecture, from syllabus to each lesson, built around COS skills' },
        { icon: StarsOutlinedIcon, title: 'AI content engine', desc: 'Classroom-ready slides, videos, visuals, and activities' },
        { icon: AssignmentTurnedInOutlinedIcon, title: 'Embedded assessment', desc: 'Pre and post assessments that measure progress against the COS Assessment' },
        { icon: PaletteOutlinedIcon, title: 'Style controls', desc: 'Adjustable graphic style and cultural context across the whole program' },
    ];

    const assessmentFeatures = [
        { title: 'Clear Insights', desc: 'Help your learners rapidly identify their strengths and areas needing improvement in AI readiness with individual scorecards.' },
        { title: 'Reports for All Stakeholders', desc: 'Parent reports and school dashboards to monitor progress and levels of AI readiness.' },
        { title: 'Evidence-Based Decisions', desc: 'Enable your clients to confidently inform curriculum development and policy-making with solid, actionable data.' },
        { title: 'Curate to Your Target Audience', desc: 'Select from a list of indicators that you want to assess for any demographic.' },
    ];

    const cpciAssessmentSections = [
        {
            heading: 'What It Includes',
            items: [
                '179 questions across 7 sections, about 40 to 50 minutes',
                'Delivered across all your schools, with results shown on one dashboard for the whole network',
            ],
        },
        {
            heading: 'What It Does',
            items: [
                "Measures your children's readiness for online safety, from everyday habits to safety skills",
                'Measures their exposure to online risks like cyberbullying, unwanted contact, and harmful content',
                'Reads the context and support around them, at home and at school',
            ],
        },
        {
            heading: 'What It Delivers',
            items: [
                "A network dashboard of children's scorecards across every school",
                'Comparison against national and global benchmarks',
                'Priority areas that need the most support',
            ],
        },
    ];

    return (
        <>
            <Box
                sx={{
                    px: { xs: 2, sm: 4, md: 0 },
                    py: { xs: 3, sm: 5 },
                    maxWidth: '1200px',
                    mx: 'auto',
                }}
            >
                {/* Page heading */}
                <Typography
                    sx={{
                        textAlign: 'center',
                        color: ORANGE,
                        fontSize: { xs: '28px', sm: '36px' },
                        fontWeight: 700,
                        mb: 1,
                    }}
                >
                    Create
                </Typography>
                <Typography
                    sx={{
                        textAlign: 'center',
                        color: 'var(--brand-text, #374151)',
                        fontSize: { xs: '15px', sm: '16px' },
                        mb: 5,
                    }}
                >
                    Choose what you'd like to build — a full learning program or a focused assessment.
                </Typography>

                {/* Cards */}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        gap: 3,
                        alignItems: 'stretch',
                        justifyContent: 'center',
                    }}
                >
                    {hasLearningPrograms && (
                        <CreateCard
                            icon={ProgramInfoIcon}
                            badgeColor={ORANGE}
                            title="Create custom Child Online Safety learning programs"
                            subtitle="LearnCreator — for Child Online Safety programs"
                            description={[
                                'LearnCreator helps you build and sell your own branded Child Online Safety programs even without curriculum-design expertise.',
                                'Start building each program from the gaps the COS Assessment reveals, and deploy it to the schools that need it most.',
                            ]}
                            featuresHeading="What's included"
                            features={programFeatures}
                            featureGrid
                            ctaLabel="Start building"
                            onCta={handleCreateProgram}
                            secondaryCtaLabel="Browse templates"
                            onSecondaryCta={() => history.push('/dq_pro/learning/program/list')}
                            singleCard={singleCard}
                        />
                    )}

                    {hasAssessments && (
                        <CreateCard
                            icon={AccessInfoIcon}
                            badgeColor="#7B8FC7"
                            title="Run an Assessment"
                            subtitle="AI Readiness Assessment"
                            features={assessmentFeatures}
                            ctaLabel="Start Assessment"
                            onCta={handleCreateAssessment}
                            singleCard={singleCard}
                        />
                    )}

                    {hasCpciAssessments && (
                        <CreateCard
                            icon={AccessInfoIcon}
                            badgeColor="#0097B2"
                            title="Measure Your Children’s Online Safety"
                            subtitle="Children Assessment"
                            sections={cpciAssessmentSections}
                            ctaLabel="Preview Assessment"
                            onCta={() => handlePreview(CPCI_ITEM)}
                            singleCard={singleCard}
                        />
                    )}

                    {/* Slide 180 — Learning Program Requests card, LAST position.
                        Equal width + STRETCH to the siblings' height; content is
                        distributed top→bottom (space-between) with a short value list
                        so a low-count card still fills the height cleanly.
                        enterprise_p1 only — see hasProgramRequests. */}
                    {hasProgramRequests && (
                        <Box
                            onClick={() => history.push('/dq_pro/program/requests')}
                            sx={{
                                // Same WIDTH as siblings, but height fits its own content
                                // (top-aligned) — a very tall neighbour won't stretch it
                                // into a sparse, over-tall box.
                                width: '100%', flex: { md: '1 1 0' }, minWidth: 0,
                                maxWidth: singleCard ? { xs: '100%', md: 720 } : { md: 'calc(50% - 12px)' },
                                alignSelf: { md: 'flex-start' },
                                border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '20px',
                                boxShadow: '0px 4px 16px rgba(0,0,0,0.06)',
                                backgroundColor: 'var(--brand-surface, #ffffff)',
                                display: 'flex', flexDirection: 'column', overflow: 'hidden', cursor: 'pointer',
                                transition: 'box-shadow 0.2s ease',
                                '&:hover': { boxShadow: '0px 6px 20px rgba(0,0,0,0.10)' },
                            }}
                        >
                            <Box sx={{ backgroundColor: ORANGE, px: { xs: 3, sm: 4 }, py: 2 }}>
                                <Typography sx={{ color: '#fff', fontSize: '13px', fontWeight: 700, letterSpacing: '0.6px', textTransform: 'uppercase' }}>
                                    Learning Program Requests
                                </Typography>
                            </Box>
                            {/* Natural, compact stacking — icon, count, description, button. */}
                            <Box sx={{
                                p: { xs: 3, sm: 4 },
                                display: 'flex', flexDirection: 'column', alignItems: 'center',
                                textAlign: 'center', gap: 2.5,
                            }}>
                                <Box sx={{
                                    width: 80, height: 80, borderRadius: '16px',
                                    backgroundColor: 'var(--brand-surface-subtle, #FFF4ED)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}>
                                    <AssignmentOutlinedIcon sx={{ fontSize: 44, color: ORANGE }} />
                                </Box>

                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                    <Typography sx={{ fontSize: '64px', fontWeight: 800, color: ORANGE, lineHeight: 1 }}>
                                        {requestCount === null ? '—' : requestCount}
                                    </Typography>
                                    <Typography sx={{ fontSize: '20px', fontWeight: 700, color: 'var(--brand-text, #111827)', mt: 0.5 }}>
                                        {requestCount === 1 ? 'Request' : 'Requests'}
                                    </Typography>
                                </Box>

                                <Typography sx={{ fontSize: '15px', color: 'var(--brand-text-secondary, #4B5563)', lineHeight: 1.6, maxWidth: 320 }}>
                                    View requests from schools to create Child Online Safety programs tailored to their needs.
                                </Typography>

                                <Button
                                    onClick={(e) => { e.stopPropagation(); history.push('/dq_pro/program/requests'); }}
                                    endIcon={<CallMadeIcon sx={{ fontSize: 16 }} />}
                                    sx={{
                                        backgroundColor: ORANGE, color: '#fff', textTransform: 'none',
                                        fontWeight: 700, fontSize: '15px', px: 4, py: 1.25, borderRadius: '10px',
                                        boxShadow: 'none', '&:hover': { backgroundColor: '#e55e00', boxShadow: 'none' },
                                    }}
                                >
                                    View Requests
                                </Button>
                            </Box>
                        </Box>
                    )}
                </Box>
            </Box>

            {/* Existing program confirmation dialog */}
            <Dialog
                open={showExistingDialog}
                onClose={() => setShowExistingDialog(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: '24px',
                        boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
                        overflow: 'hidden',
                        p: 0,
                    },
                }}
                BackdropProps={{ style: { backgroundColor: 'rgba(0,0,0,0.4)' } }}
            >
                <Box sx={{ position: 'relative', p: { xs: 3, sm: 4 }, textAlign: 'center' }}>
                    <IconButton
                        aria-label="close"
                        onClick={() => setShowExistingDialog(false)}
                        sx={{ position: 'absolute', right: 12, top: 12, color: '#999' }}
                    >
                        <CloseIcon />
                    </IconButton>

                    <Typography sx={{ fontSize: '20px', fontWeight: 700, color: 'var(--brand-text, #111827)', mt: 2, mb: 1 }}>
                        Existing Program Found
                    </Typography>
                    <Typography sx={{ fontSize: '15px', color: 'var(--brand-text, #374151)', mb: 4, lineHeight: 1.6 }}>
                        You already have a program in progress.<br />
                        Would you like to <strong>continue</strong> where you left off, or <strong>start over</strong> with a new program?
                    </Typography>

                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
                        <Button
                            onClick={handleStartNew}
                            sx={{
                                minWidth: 150,
                                px: 3,
                                py: 1.25,
                                borderRadius: '10px',
                                backgroundColor: 'var(--brand-surface, #fff)',
                                color: 'var(--brand-text, #111)',
                                fontWeight: 700,
                                fontSize: '15px',
                                textTransform: 'none',
                                border: '1.5px solid var(--brand-text, #111)',
                                '&:hover': { backgroundColor: 'var(--brand-surface, #f7f7f7)' },
                            }}
                        >
                            Start Over
                        </Button>
                        <Button
                            onClick={() => { setShowExistingDialog(false); history.push('/dq_pro/program/creation/preview'); }}
                            sx={{
                                minWidth: 150,
                                px: 3,
                                py: 1.25,
                                borderRadius: '10px',
                                backgroundColor: ORANGE,
                                color: '#fff',
                                fontWeight: 700,
                                fontSize: '15px',
                                textTransform: 'none',
                                boxShadow: 'none',
                                '&:hover': { backgroundColor: '#e55e00', boxShadow: 'none' },
                            }}
                        >
                            Continue
                        </Button>
                    </Box>
                </Box>
            </Dialog>
        </>
    );
};

export default DQProCreate;
